import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editDepositBalance, setEditDepositBalance] = useState('');
  const [editInterestBalance, setEditInterestBalance] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [savingDepositBalance, setSavingDepositBalance] = useState(false);
  const [savingInterestBalance, setSavingInterestBalance] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const load = (s = '') => {
    setLoading(true);
    adminAPI.users({ search: s, limit: 50 })
      .then(r => setUsers(r.data.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const closeModal = () => {
    setSelected(null);
    setEditDepositBalance('');
    setEditInterestBalance('');
    setNewPassword('');
  };

  const toggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      await adminAPI.updateUser(user.id, { status: newStatus });
      toast.success(`User ${newStatus}`);
      load(search);
    } catch { toast.error('Failed'); }
  };

  const updateDepositBalance = async () => {
    if (editDepositBalance === '') return toast.error('Please enter a deposit balance');
    const bal = parseFloat(editDepositBalance);
    if (isNaN(bal) || bal < 0) return toast.error('Please enter a valid balance');
    setSavingDepositBalance(true);
    try {
      await adminAPI.updateUser(selected.id, { deposit_balance: bal });
      toast.success('Deposit balance updated');
      setEditDepositBalance('');
      load(search);
      setSelected(prev => ({
        ...prev,
        deposit_balance: bal,
        balance: bal + parseFloat(prev.interest_balance || 0),
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update deposit balance');
    } finally {
      setSavingDepositBalance(false);
    }
  };

  const updateInterestBalance = async () => {
    if (editInterestBalance === '') return toast.error('Please enter an interest balance');
    const bal = parseFloat(editInterestBalance);
    if (isNaN(bal) || bal < 0) return toast.error('Please enter a valid balance');
    setSavingInterestBalance(true);
    try {
      await adminAPI.updateUser(selected.id, { interest_balance: bal });
      toast.success('Interest balance updated');
      setEditInterestBalance('');
      load(search);
      setSelected(prev => ({
        ...prev,
        interest_balance: bal,
        balance: parseFloat(prev.deposit_balance || 0) + bal,
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update interest balance');
    } finally {
      setSavingInterestBalance(false);
    }
  };

  const setUserPassword = async () => {
    if (!newPassword || newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setSavingPassword(true);
    try {
      await adminAPI.setUserPassword(selected.id, { new_password: newPassword });
      toast.success('Password updated successfully');
      setNewPassword('');
    } catch (err) {
      console.error('setUserPassword error:', err);
      toast.error(err.response?.data?.message || 'Failed to set password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div>
      <div className="filters">
        <input className="search-input" placeholder="Search by username or email..."
          value={search}
          onChange={e => { setSearch(e.target.value); load(e.target.value); }} />
        <span style={{ color: 'var(--gray-400)', fontSize: 13 }}>{users.length} users</span>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-title">Edit User: {selected.username}</div>
            <div style={{ marginBottom: 16 }}>
              <p><strong>Email:</strong> {selected.email}</p>
              <p><strong>Total Balance:</strong> GHS {parseFloat(selected.balance || 0).toFixed(2)}</p>
              <p><strong>Deposit Balance:</strong> GHS {parseFloat(selected.deposit_balance || 0).toFixed(2)}</p>
              <p><strong>Interest Balance:</strong> GHS {parseFloat(selected.interest_balance || 0).toFixed(2)}</p>
              <p><strong>Status:</strong> <span className={`badge badge-${selected.status}`}>{selected.status}</span></p>
            </div>

            {/* Deposit Balance Section */}
            <div style={{ background: 'var(--gray-50, #f9fafb)', border: '1px solid var(--gray-200)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>💵 Set Deposit Balance</div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">New Deposit Balance (GHS)</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editDepositBalance}
                  onChange={e => setEditDepositBalance(e.target.value)}
                  placeholder={`Current: GHS ${parseFloat(selected.deposit_balance || 0).toFixed(2)}`}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={updateDepositBalance}
                disabled={savingDepositBalance || editDepositBalance === ''}
                style={{ width: '100%' }}
              >
                {savingDepositBalance ? 'Saving...' : 'Set Deposit Balance'}
              </button>
            </div>

            {/* Interest Balance Section */}
            <div style={{ background: 'var(--gray-50, #f9fafb)', border: '1px solid var(--gray-200)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>📈 Set Interest Balance</div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">New Interest Balance (GHS)</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editInterestBalance}
                  onChange={e => setEditInterestBalance(e.target.value)}
                  placeholder={`Current: GHS ${parseFloat(selected.interest_balance || 0).toFixed(2)}`}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={updateInterestBalance}
                disabled={savingInterestBalance || editInterestBalance === ''}
                style={{ width: '100%', background: '#10b981', borderColor: '#10b981' }}
              >
                {savingInterestBalance ? 'Saving...' : 'Set Interest Balance'}
              </button>
            </div>

            {/* Password Section */}
            <div style={{ background: 'var(--gray-50, #f9fafb)', border: '1px solid var(--gray-200)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>🔑 Set New Password</div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">New Password (min 6 characters)</label>
                <input
                  className="form-control"
                  type="text"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password for user"
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={setUserPassword}
                disabled={savingPassword || !newPassword}
                style={{ width: '100%', background: '#f59e0b', borderColor: '#f59e0b' }}
              >
                {savingPassword ? 'Updating...' : '🔑 Set Password'}
              </button>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="table-card">
        <div className="table-header"><h3>All Users</h3></div>
        {loading ? <div className="loading"><div className="spinner" /></div> :
          <table>
            <thead>
              <tr>
                <th>ID</th><th>Username</th><th>Email</th><th>Balance</th>
                <th>Total Earned</th><th>Ref Code</th><th>Status</th><th>Joined</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td style={{ fontWeight: 500 }}>{u.username}</td>
                  <td>{u.email}</td>
                  <td style={{ fontWeight: 600, color: 'var(--success)' }}>GHS {parseFloat(u.balance).toFixed(2)}</td>
                  <td>GHS {parseFloat(u.cumulative_income).toFixed(2)}</td>
                  <td><code style={{ background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{u.referral_code}</code></td>
                  <td><span className={`badge badge-${u.status}`}>{u.status}</span></td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => {
                        setSelected(u);
                        setEditDepositBalance('');
                        setEditInterestBalance('');
                        setNewPassword('');
                      }}>Edit</button>
                      <button className={`btn btn-sm ${u.status === 'active' ? 'btn-danger' : 'btn-success'}`} onClick={() => toggleStatus(u)}>
                        {u.status === 'active' ? 'Suspend' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
}